import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import {
  UntypedFormArray,
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import { ActivatedRoute, Router } from '@angular/router';
import { BehaviorSubject, Subject, Subscription } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

// CONSTANTS
import { EFeatureFlag } from '@app/feature-toggle-list';

import { IAppIntegrationAccount, IChannel, SnackBarConfig } from '../../../shared';
import {
  ChannelApiService,
  EnvironmentUtilService,
  FeatureFlagUtilService,
} from '../../../shared/services';
import { EAppAccountType } from '../../../system-setting/constants';
import {
  StoreTypeConstant,
  StoreTypesListConstant,
  WoocommerceStoreConstant,
} from '../../contants/store-types.contant';

// INTERFACES
import { IStoreType } from '../../interfaces';

// SERVICES

@Component({
  selector: 'lum-channel-form',
  templateUrl: './channel-form.component.html',
  styleUrls: ['./channel-form.component.scss'],
})
export class ChannelFormComponent implements OnInit, OnDestroy {
  private readonly _loading = new BehaviorSubject<boolean>(<boolean>false);
  loading$ = this._loading.asObservable();

  formData :UntypedFormGroup;

  public storeTypesList :IStoreType[] = StoreTypesListConstant;
  public storeTypeConstant = StoreTypeConstant;

  public appIntegrationAccounts$ :Subscription;
  private onDestroy$ = new Subject();

  channel :IChannel;
  selectedAppIntegrationAccount :Partial<IAppIntegrationAccount>;
  selectedPushOrderAppIntegrationAccount :Partial<IAppIntegrationAccount>;
  featureFlags :{ [key :string] :boolean };
  appAccountType = EAppAccountType;
  submitted :boolean = false;

  environment = this.environmentUtilService.getEnvironment();

  constructor(
    private fb :UntypedFormBuilder,
    private channelApiService :ChannelApiService,
    private router :Router,
    private activatedRoute :ActivatedRoute,
    private snackBar :MatSnackBar,
    private cd :ChangeDetectorRef,
    private route :ActivatedRoute,
    private featureFlagUtilService :FeatureFlagUtilService,
    private environmentUtilService :EnvironmentUtilService,
  ) {}

  ngOnDestroy() :void {
    this.onDestroy$.next(null);
    this.onDestroy$.complete();
  }

  ngOnInit() :void {
    this.initFeatureFlags();
    this.initStoreTypesList();
    this.initForm();
    this.parseRouteParams();
  }

  onAppAccountSelect(data :Partial<IAppIntegrationAccount>) :void {
    // Check if the data object and its credentials property exist
    if (data && data?.credentials) {
      // Set the warehouse_id in formData with the value from data.credentials.warehouse_id
      const warehouseId =
        this.channel?.credentials?.warehouse_id ?? data?.credentials?.warehouse_id;
      this.formData.get('credentials')?.get('warehouse_id')?.setValue(warehouseId);
    }
  }

  private parseRouteParams() {
    this.activatedRoute.params.subscribe((params :any) => {
      if (params.id) {
        this.initFormWithData(params.id);
      } else {
        this.initForm();
      }
    });
  }

  private initFormWithData(channelId :number) {
    const params :any = {
      relations:
        'pullOrderIntegrationMapping,pushOrderIntegrationMapping,tags,getProductsIntegrationMapping',
    };
    this._loading.next(true);
    this.channelApiService
      .getById(channelId, params)
      .pipe(takeUntil(this.onDestroy$))
      .subscribe({
        next: (channel :IChannel) => {
          this.channel = channel;
          this.initForm(channel);
        },
        complete: () => {
          this._loading.next(false);
        },
      });
  }

  private initForm(channel ?:IChannel) {
    this.formData = this.fb.group({
      label: [channel?.label, Validators.required],
      description: [channel?.description],
      warehouseGroupId: [channel?.warehouseGroupId, Validators.required],

      appIntegrationAccountId: [
        channel?.pullOrderIntegrationMapping?.appIntegrationAccountId,
      ],
      omsStoreId: [channel?.pullOrderIntegrationMapping?.omsStoreId],
      pullOrdersFromOms: [channel?.pullOrderIntegrationMapping ? true : false],

      pushOrderAppIntegrationAccountId: [
        channel?.pushOrderIntegrationMapping?.appIntegrationAccountId,
      ],
      pushOrderOmsStoreId: [channel?.pushOrderIntegrationMapping?.omsStoreId],
      pushOrdersToOms: [channel?.pushOrderIntegrationMapping ? true : false],
      pushOrdersToOmsOptions: this.fb.group({
        pushStock: [
          channel?.pushOrderIntegrationMapping?.options?.pushStock || false,
        ],
        omsShippingMethodId: [
          channel?.pushOrderIntegrationMapping?.options?.omsShippingMethodId ||
            null,
        ],
        autoPushOrderAfterMinutes: [
          channel?.pushOrderIntegrationMapping?.options
            ?.autoPushOrderAfterMinutes || null,
        ],
        pushCustomFields: this.fb.array([]),
      }),

      getProductsEnabled: [channel?.getProductsEnabled],
      storeType: [channel?.storeType],
      pushInventoryEnabled: [channel?.pushInventoryEnabled],
      includeIncomingStocksEnabled: [channel?.includeIncomingStocksEnabled],
      pullStocksEnabled: [channel?.pullStocksEnabled],
      pullShipmentsEnabled: [channel?.pullShipmentsEnabled],
      getProductsAppIntegrationAccountId: [
        channel?.getProductsIntegrationMapping?.appIntegrationAccountId,
        Validators.required,
      ],

      credentials: this.fb.group({
        ...channel?.credentials,
        warehouse_id: [channel?.credentials?.warehouse_id], // Initialize warehouse_id here
      }),
      fees:
        Array.isArray(this.channel?.fees) && this.channel?.fees.length
          ? this.fb.array(
              this.channel.fees.map((fee) =>
                this.fb.group({
                  label: [fee.label, Validators.required],
                  amount: [fee.amount, Validators.required],
                  type: [fee.type, Validators.required],
                }),
              ),
            )
          : this.fb.array([]),
      tagIds: this.channel?.tags?.length
        ? this.fb.array(this.channel.tags.map((tag) => tag.id))
        : this.fb.array([]),
      filters: this.channel?.filters?.length
        ? this.fb.array(
            this.channel.filters.map((filter) =>
              this.fb.group({
                key: [filter.key, Validators.required],
                condition: [filter.condition, Validators.required],
                value: [filter.value, Validators.required],
              }),
            ),
          )
        : this.fb.array([]),
    });

    if (
      channel?.pushOrderIntegrationMapping?.options?.pushCustomFields ||
      channel?.pushOrderIntegrationMapping?.options?.pushCustomFields?.length
    ) {
      const pushCustomFields = this.formData
        .get('pushOrdersToOmsOptions')
        .get('pushCustomFields') as UntypedFormArray;
      channel.pushOrderIntegrationMapping.options.pushCustomFields.forEach(
        (field) => {
          if (field && field.key && field.value) {
            pushCustomFields.push(
              this.fb.group({
                key: [field.key, Validators.required],
                value: [field.value, Validators.required],
              }),
            );
          }
        },
      );
    }

    this.cd.detectChanges();
  }

  private initFeatureFlags() :void {
    this.featureFlags = this.route.snapshot.data['featureFlags'];
  }

  private initStoreTypesList() :void {
    if (this.ftRemoveWoocommerceFromChannelType) {
      this.storeTypesList = this.storeTypesList.filter(
        (source) => source.id !== WoocommerceStoreConstant.id,
      );
    }
  }

  get pushOrdersToOmsOptions() :UntypedFormGroup {
    return this.formData.get('pushOrdersToOmsOptions') as UntypedFormGroup;
  }

  get ftRemoveWoocommerceFromChannelType() :boolean {
    if (this.featureFlags?.hasOwnProperty('ftRemoveWoocommerceFromChannelType')) {
      return this.featureFlags['ftRemoveWoocommerceFromChannelType'];
    }

    return false;
  }

  get fees() :UntypedFormArray {
    return this.formData.get('fees') as UntypedFormArray;
  }

  get filters() :UntypedFormArray {
    return this.formData.get('filters') as UntypedFormArray;
  }

  get credentials() :UntypedFormGroup {
    return this.formData.get('credentials') as UntypedFormGroup;
  }
  get shouldGetProducts() :boolean {
    return this.formData.value.getProductsEnabled;
  }

  get shouldPullOrders() :boolean {
    return this.formData.value.pullOrdersFromOms;
  }

  get shouldPushOrders() :boolean {
    return this.formData.value.pushOrdersToOms;
  }

  get selectedStoreType() :number {
    return parseInt(this.formData.value.storeType);
  }

  get selectedAppIntegrationAccountId() :number {
    return parseInt(this.formData.value.appIntegrationAccountId);
  }

  get showChannelFilters() :boolean {
    return (
      this.selectedAppIntegrationAccount?.app &&
      this.selectedAppIntegrationAccountId > 0
    );
  }

  get showPushOrderCustomFields() :boolean {
    return (
      this.shouldPushOrders &&
      this.selectedPushOrderAppIntegrationAccount &&
      this.selectedPushOrderAppIntegrationAccount.app === EAppAccountType.TRACKSTAR
    );
  }

  get ediPushOrderToOmsAppTypes() :EAppAccountType[] {
    const suppotedAppTypes = [
      EAppAccountType.SHIPSTATION,
      EAppAccountType.TRACKSTAR,
    ];

    return suppotedAppTypes;
  }

  get selectedPushOrderAppIntegrationAccountId() :number {
    return parseInt(this.formData.value.pushOrderAppIntegrationAccountId);
  }

  private selectedAppHasStores(app :string) :boolean {
    if (app.length === 0) {
      return false;
    }
    const withStoresOms :string[] = [
      EAppAccountType.SHIPKEY,
      EAppAccountType.SHIPSTATION,
      EAppAccountType.TRACKSTAR,
    ];

    return withStoresOms.includes(app);
  }

  get showOmsStoresDropdown() :boolean {
    return (
      this.selectedAppIntegrationAccountId > 0 &&
      this.selectedAppIntegrationAccount &&
      this.selectedAppHasStores(this.selectedAppIntegrationAccount.app || '')
    );
  }

  get showPushOrderOmsStoresDropdown() :boolean {
    return (
      this.selectedPushOrderAppIntegrationAccountId > 0 &&
      this.selectedPushOrderAppIntegrationAccount &&
      this.selectedAppHasStores(
        this.selectedPushOrderAppIntegrationAccount.app || '',
      )
    );
  }

  get mode() :string {
    return this.channel ? 'Update' : 'Create';
  }

  get tagIds() :UntypedFormArray {
    return this.formData.get('tagIds') as UntypedFormArray;
  }

  get ftEdiIntegrationEnabled() :boolean {
    return this.featureFlagUtilService.isFeatureEnabled(
      EFeatureFlag.EDI_INTEGRATION,
    );
  }

  get showPushOrders() :boolean {
    return (
      this.ftEdiIntegrationEnabled &&
      this.selectedAppIntegrationAccount?.app === EAppAccountType.EDI
    );
  }

  get showPushStocksToggle() :boolean {
    return this.showPushOrders && this.formData.get('pushOrdersToOms')?.value;
  }

  get shouldSetShppingMethod() :boolean {
    return (
      this.shouldPushOrders &&
      this.selectedPushOrderAppIntegrationAccount?.app === EAppAccountType.TRACKSTAR
    );
  }

  get ediWebhookUrl() :string {
    if (this.selectedAppIntegrationAccount?.app === EAppAccountType.EDI) {
      if (this.channel?.id && this.channel.uuid) {
        return `${this.environment.url}/api/edi/${this.channel.uuid}`;
      }

      return 'Webhook url will be generated after this channel will be created.';
    }

    return '';
  }

  get selectedGetProductsStoreType() :number {
    return parseInt(this.formData.value.storeType);
  }

  get showGetProductsOmsDropdown() :boolean {
    return (
      this.selectedGetProductsStoreType > 0 &&
      this.selectedGetProductsStoreType === StoreTypeConstant.TRACKSTAR
    );
  }

  get showGetProductsToggle() :boolean {
    return this.selectedAppIntegrationAccount?.app !== EAppAccountType.VESYL;
  }

  onAppAccountSelected(appIntegrationAccount :Partial<IAppIntegrationAccount>) {
    this.selectedAppIntegrationAccount = appIntegrationAccount;
  }

  onPushOrderAppAccountSelected(
    appIntegrationAccount :Partial<IAppIntegrationAccount>,
  ) {
    this.selectedPushOrderAppIntegrationAccount = appIntegrationAccount;
  }

  onClose() {
    // this.dialogRef.close();
    this.router.navigateByUrl('/channels');
  }

  onSubmit() {
    this.submitted = true;
    const channelData :Partial<IChannel> = this.formData.value;
    if (
      !channelData.getProductsAppIntegrationAccountId &&
      this.selectedStoreType === this.storeTypeConstant.TRACKSTAR
    ) {
      this.snackBar.open('Need to select Products from OMS.', 'Fail', {
        duration: SnackBarConfig.DEFAULT.duration,
      });

      return;
    }
    if (channelData.getProductsEnabled && !channelData.storeType) {
      this.snackBar.open(
        "Please select a Store Type in the 'Get Products From' section.",
        'Fail',
        {
          duration: SnackBarConfig.DEFAULT.duration,
        },
      );

      return;
    }
    if (!channelData.pullOrdersFromOms) {
      delete channelData.appIntegrationAccountId;
      delete channelData.omsStoreId;
    } else if (
      !this.selectedAppHasStores(this.selectedAppIntegrationAccount?.app || '')
    ) {
      delete channelData.omsStoreId;
    }
    if (!this.showPushOrders) {
      delete channelData.pushOrderIntegrationMapping;
    }
    if (
      !channelData.getProductsEnabled ||
      this.selectedAppIntegrationAccount?.app === EAppAccountType.VESYL
    ) {
      channelData.getProductsEnabled = false;
      delete channelData.storeType;
      delete channelData.credentials;
      delete channelData.pushInventoryEnabled;
      delete channelData.includeIncomingStocksEnabled;
      delete channelData.pullStocksEnabled;
      delete channelData.pullShipmentsEnabled;
    }
    if (this.channel) {
      return this.update(channelData);
    }

    return this.create(channelData);
  }

  private update(channel :Partial<IChannel>) {
    this._loading.next(true);
    this.channelApiService
      .update(this.channel.id, channel)
      .pipe(takeUntil(this.onDestroy$))
      .subscribe({
        next: (channel :IChannel) => {
          this._loading.next(false);
          this.snackBar.open('Channel updated', 'Close', {
            duration: 3000,
          });
          this.onClose();
        },
        error: (err) => {
          this._loading.next(false);
          const message = err?.error || 'Please input all required fields';
          this.snackBar.open(message, 'Close', {
            duration: 3000,
          });
        },
      });
  }

  private create(channel :Partial<IChannel>) {
    this._loading.next(true);
    this.channelApiService
      .create(channel)
      .pipe(takeUntil(this.onDestroy$))
      .subscribe({
        next: (channel :IChannel) => {
          this._loading.next(false);
          this.snackBar.open('Channel created', 'Close', {
            duration: 3000,
          });
          this.onClose();
        },
        error: (err) => {
          this._loading.next(false);
          const message = err?.error || 'Please input all required fields';
          this.snackBar.open(message, 'Close', {
            duration: 3000,
          });
        },
      });
  }
}
